import api, { transformRequest, transformResponse } from "services/api";

export const debtorAccounts = api
  .enhanceEndpoints({
    addTagTypes: [
      "Disputes",
      "Accounts",
      "DebtorAccounts",
      "CreditorCost",
      "DebtorSystemLogs",
      "DebtorDisputes",
      "Documents",
      "LegalFees",
      "AccountBalanceHistory",
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      fetchDebtorAccounts: build.query({
        query: (payload) => ({
          url: `/api/v1/core/debtors/${payload.debtorId}/accounts/`,
          method: "GET",
          params: transformRequest(payload),
        }),
        transformResponse: (response) => transformResponse(response),
        providesTags: (results) =>
          results
            ? // successful query
              [
                ...results.map(({ id }) => ({ type: "DebtorAccounts", id })),
                { type: "DebtorAccounts", id: "LIST" },
              ]
            : // an error occurred, but we still want to refetch this query when `{ type: 'DebtorAccounts', id: 'LIST' }` is invalidated
              [{ type: "DebtorAccounts", id: "LIST" }],
      }),
      fetchDebtorSystemLogs: build.query({
        query: (payload) => `/api/v1/common/debtors/${payload.debtorId}/system-logs/`,
        transformResponse: (response) => transformResponse(response),
        providesTags: (results) =>
          results
            ? [{ type: "DebtorSystemLogs", id: "LIST" }]
            : [{ type: "DebtorSystemLogs", id: "LIST" }],
      }),
      fetchAccountBalanceHistory: build.query({
        query: (payload) => `/api/v1/core/accounts/${payload.accountId}/balance-history/`,
        transformResponse: (response) => transformResponse(response),
        providesTags: (results, err, arg) => [{ type: "AccountBalanceHistory", id: arg.accountId }],
      }),
      createTask: build.mutation({
        query: (payload) => ({
          url: "/api/v1/core/communication/tasks/create/",
          method: "POST",
          body: transformRequest(payload),
        }),
        invalidatesTags: [
          { type: "Disputes", id: "LIST" },
          { type: "DebtorSystemLogs", id: "LIST" },
        ],
      }),
      fetchSequenceTasks: build.query({
        query: (payload) => ({
          url: "/api/v1/core/communication/tasks/",
          method: "POST",
          body: transformRequest(payload),
        }),
        transformResponse: (response) => transformResponse(response),
      }),
      updateAccountPaymentData: build.mutation({
        query: (payload) => ({
          url: `/api/v1/core/accounts/${payload.accountId}/update-payment-data/`,
          method: "POST",
          body: transformRequest(payload),
        }),
        transformResponse: (response) => transformResponse(response),
        // Invalidates all DebtorAccounts-type and Lawsuits queries providing the `LIST` id - after all, depending on the sort order,
        invalidatesTags: [
          { type: "Disputes", id: "LIST" },
          { type: "DebtorAccounts", id: "LIST" },
          { type: "Lawsuits", id: "LIST" },
        ],
      }),
      createAccountDebtor: build.mutation({
        query: ({ accountId, ...payload }) => ({
          url: `/api/v1/core/accounts/${accountId}/debtors/create/`,
          method: "POST",
          body: transformRequest(payload),
        }),
        transformResponse: (response) => transformResponse(response),
        // Invalidates all DebtorAccounts-type queries providing the `LIST` id - after all, depending of the sort order,
        invalidatesTags: [
          { type: "Disputes", id: "LIST" },
          { type: "DebtorAccounts", id: "LIST" },
        ],
      }),
      linkAccountDebtor: build.mutation({
        query: (payload) => ({
          url: `/api/v1/core/accounts/${payload.accountId}/debtors/link/`,
          method: "POST",
          body: transformRequest(payload),
        }),
        invalidatesTags: [
          { type: "Disputes", id: "LIST" },
          { type: "DebtorAccounts", id: "LIST" },
        ],
      }),
      unlinkAccountDebtor: build.mutation({
        query: (payload) => ({
          url: `/api/v1/core/accounts/${payload.accountId}/debtors/unlink/`,
          method: "POST",
          body: transformRequest(payload),
        }),
        invalidatesTags: [
          { type: "Disputes", id: "LIST" },
          { type: "DebtorAccounts", id: "LIST" },
        ],
      }),
      updateFollowUpDate: build.mutation({
        query: (payload) => ({
          url: `/api/v1/core/accounts/${payload.accountId}/update-follow-up-date/`,
          method: "POST",
          body: transformRequest(payload),
        }),
        transformResponse: (response) => transformResponse(response),
        // Invalidates all DebtorAccounts-type queries providing the `LIST` id - after all, depending of the sort order,
        invalidatesTags: [
          { type: "Disputes", id: "LIST" },
          { type: "DebtorAccounts", id: "LIST" },
        ],
      }),
      fetchAccount: build.query({
        query: (payload) => `/api/v1/core/accounts/${payload.accountId}/`,
        transformResponse: (response) => transformResponse(response),
      }),
      deleteAccount: build.mutation({
        query: (payload) => ({
          url: `/api/v1/core/accounts/${payload.accountId}/delete/`,
          method: "POST",
          body: transformRequest(payload.accountId),
        }),
        invalidatesTags: [
          { type: "Disputes", id: "LIST" },
          { type: "DebtorAccounts", id: "LIST" },
          { type: "LegalFees", id: "LIST" },
        ],
      }),
      updateAccount: build.mutation({
        query: (payload) => ({
          url: `/api/v1/core/accounts/${payload.accountId}/update/`,
          method: "POST",
          body: transformRequest(payload),
          headers: { "aktos-suppress-error": "true" },
        }),
        transformResponse: (response) => transformResponse(response),
        invalidatesTags: (result, error, arg) => [
          { type: "Disputes", id: "LIST" },
          { type: "DebtorAccounts", id: "LIST" },
          { type: "DebtorSystemLogs", id: "LIST" },
          { type: "AccountBalanceHistory", id: arg.id },
        ],
      }),
      updateAccountStatus: build.mutation({
        query: (payload) => ({
          url: `/api/v1/core/workflow-tasks/${payload.workflowTaskId}/update/`,
          method: "POST",
          body: transformRequest(payload),
        }),
        transformResponse: (response) => transformResponse(response),
        // Invalidates all DebtorAccounts-type queries providing the `LIST` id - after all, depending of the sort order,
        invalidatesTags: [
          { type: "Disputes", id: "LIST" },
          { type: "DebtorAccounts", id: "LIST" },
          { type: "DebtorSystemLogs", id: "LIST" },
        ],
      }),
      postAssignAccountsToForwardingEntity: build.mutation({
        query: (payload) => ({
          url: `/api/v1/core/accounts/update-forwarding-entity/`,
          method: "POST",
          body: transformRequest(payload),
        }),
        transformResponse: (response) => transformResponse(response),
        // Invalidates all DebtorAccounts-type queries providing the `LIST` id - after all, depending of the sort order,
        invalidatesTags: [
          { type: "Disputes", id: "LIST" },
          { type: "DebtorAccounts", id: "LIST" },
          { type: "DebtorSystemLogs", id: "LIST" },
        ],
      }),
      createCreditorCost: build.mutation({
        query: (payload) => ({
          url: `/api/v1/core/creditor-costs/create/`,
          method: "POST",
          body: transformRequest(payload),
        }),
        transformResponse: (response) => transformResponse(response),
        // Invalidates all CreditorCost-type queries providing the `LIST` id - after all, depending of the sort order,
        invalidatesTags: [
          { type: "Disputes", id: "LIST" },
          { type: "CreditorCost", id: "LIST" },
          { type: "DebtorSystemLogs", id: "LIST" },
        ],
      }),
      createDispute: build.mutation({
        query: ({ file, ...values }) => ({
          url: `/api/v1/core/disputes/create/`,
          method: "POST",
          body: transformRequest(values),
        }),
        invalidatesTags: [
          { type: "Disputes", id: "LIST" },
          { type: "Accounts", id: "LIST" },
          { type: "DebtorAccounts", id: "LIST" },
          { type: "DebtorSystemLogs", id: "LIST" },
          { type: "DebtorDisputes", id: "LIST" },
          { type: "Documents", id: "LIST" },
        ],
      }),
      updateDispute: build.mutation({
        query: ({ disputeId, file, ...values }) => ({
          url: `/api/v1/core/disputes/${disputeId}/update/`,
          method: "POST",
          body: transformRequest(values),
        }),
        invalidatesTags: (result, error, arg) => [
          { type: "Disputes", id: "LIST" },
          { type: "Accounts", id: arg.id },
          { type: "DebtorAccounts", id: "LIST" },
          { type: "DebtorDisputes", id: "LIST" },
          { type: "DebtorSystemLogs", id: "LIST" },
          { type: "Documents", id: arg.id },
          { type: "Disputes", id: arg.id },
          { type: "Disputes", id: "LIST" },
        ],
      }),
      fetchDisputeCategories: build.query({
        query: (payload) => ({
          url: `/api/v1/core/dispute-category-tags/`,
          method: "GET",
        }),
        transformResponse: (response) => transformResponse(response),
      }),
      fetchCreditReportHistory: build.query({
        query: (payload) => ({
          url: `/api/v1/common/accounts/${payload.accountId}/credit-report-history/`,
          method: "POST",
          body: transformRequest(payload),
        }),
        transformResponse: (response) => transformResponse(response),
      }),
      assignDesksToAccount: build.mutation({
        query: (payload) => ({
          url: `/api/v1/core/accounts/${payload.id}/assign-desk/`,
          method: "POST",
          body: transformRequest(payload),
        }),
        invalidatesTags: [{ type: "DebtorAccounts", id: "LIST" }],
      }),
      fetchHiddenDesks: build.query({
        query: (payload) => `/api/v1/core/debtors/${payload.id}/hidden-accounts`,
        transformResponse: (response) => transformResponse(response),
      }),
      fetchDebtorDisputes: build.query({
        query: (payload) => ({
          url: `/api/v1/core/debtors/${payload.debtorId}/disputes/`,
          method: "GET",
        }),
        transformResponse: (response) => transformResponse(response),
        providesTags: (results) => [{ type: "DebtorDisputes", id: "LIST" }],
      }),
      bulkUpdateFollowUpDate: build.mutation({
        query: (payload) => ({
          url: `/api/v1/core/accounts/bulk-update/follow-up-date/`,
          method: "POST",
          body: transformRequest(payload),
        }),
        transformResponse: (response) => transformResponse(response),
        invalidatesTags: [{ type: "DebtorAccounts", id: "LIST" }],
      }),
      bulkUpdateStatusCode: build.mutation({
        query: (payload) => ({
          url: `/api/v1/core/accounts/bulk-update/status-code/`,
          method: "POST",
          body: transformRequest(payload),
        }),
        transformResponse: (response) => transformResponse(response),
        invalidatesTags: [{ type: "DebtorAccounts", id: "LIST" }],
      }),
    }),

    overrideExisting: false,
  });

export const {
  useFetchAccountBalanceHistoryQuery,
  useCreateTaskMutation,
  useFetchAccountQuery,
  useFetchDebtorAccountsQuery,
  useFetchSequenceTasksQuery,
  useUpdateAccountPaymentDataMutation,
  useUpdateFollowUpDateMutation,
  useUpdateAccountMutation,
  useDeleteAccountMutation,
  usePostAssignAccountsToForwardingEntityMutation,
  useCreateCreditorCostMutation,
  useUpdateAccountStatusMutation,
  useFetchDebtorSystemLogsQuery,
  useCreateAccountDebtorMutation,
  useLinkAccountDebtorMutation,
  useUnlinkAccountDebtorMutation,
  useCreateDisputeMutation,
  useUpdateDisputeMutation,
  useFetchDisputeCategoriesQuery,
  useFetchCreditReportHistoryQuery,
  useAssignDesksToAccountMutation,
  useFetchHiddenDesksQuery,
  useFetchDebtorDisputesQuery,
  useBulkUpdateFollowUpDateMutation,
  useBulkUpdateStatusCodeMutation,
} = debtorAccounts;
